import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { vzUploadXlsx } from "@/lib/verizonSalesApi";

export const XlsxUploader = ({ onDone }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [drag, setDrag] = useState(false);

  const [current, setCurrent] = useState("");
  const handle = async (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    for (const file of files) {
      if (!/\.xlsx$/i.test(file.name)) { toast.error(`${file.name}: please choose .xlsx files`); continue; }
      setProgress(0); setCurrent(file.name);
      try {
        const snap = await vzUploadXlsx(file, setProgress);
        toast.success(`${file.name}: ${snap.store_count} stores, data through ${snap.updated_through || "n/a"}${snap.mode === "backfill" ? " (saved to history)" : ""}`);
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || "Upload failed"}`);
      }
    }
    setProgress(null); setCurrent("");
    if (inputRef.current) inputRef.current.value = "";
    onDone?.();
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${drag ? "border-teal bg-teal/5" : "border-slate-200"}`}
      data-testid="vz-upload-dropzone"
    >
      <input ref={inputRef} type="file" accept=".xlsx" multiple className="hidden" onChange={(e) => handle(e.target.files)} data-testid="vz-upload-input" />
      {progress === null ? (
        <>
          <Upload className="w-7 h-7 text-teal mx-auto mb-2" />
          <p className="text-sm text-slate-700">Drag & drop Verizon DLAR <b>.xlsx</b> files here (multiple allowed)</p>
          <p className="text-xs text-slate-500 mb-3">Older end-of-month reports are saved to Monthly History only; the newest file updates the live dashboard.</p>
          <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid="vz-upload-btn">Choose file</Button>
        </>
      ) : (
        <div className="max-w-xs mx-auto space-y-2" data-testid="vz-upload-progress">
          <Loader2 className="w-6 h-6 animate-spin text-teal mx-auto" />
          <Progress value={progress} />
          <p className="text-xs text-slate-500">{current}: {progress < 90 ? `Uploading… ${progress}%` : "Processing workbook…"}</p>
        </div>
      )}
    </div>
  );
};
